// ---------------------------------------------------------------------------
// User Access Request form model + email builder.
//
// Fields mirror the "User Access Request" sheet from the Data Engineering
// Request Forms workbook. On submit, opens an Outlook compose window with
// the form summary emailed to enterprisedata@loandepot.com.
// ---------------------------------------------------------------------------

import {
  formatDate,
  OUTLOOK_COMPOSE_BASE,
  REQUEST_RECIPIENT,
} from './intake'

export interface UserAccessValues {
  requestType: string
  requestor: string
  requestedUsers: string
  platform: string[]
  accessLevel: string
  similarAccessAs: string
  businessJustification: string
  startDate: string
  approvalObtained: string
}

export const USER_ACCESS_INITIAL: UserAccessValues = {
  requestType: '',
  requestor: '',
  requestedUsers: '',
  platform: [],
  accessLevel: 'Read Only (Default)',
  similarAccessAs: '',
  businessJustification: '',
  startDate: formatDate(new Date()),
  approvalObtained: '',
}

const SUMMARY_SECTIONS: {
  title: string
  fields: [keyof UserAccessValues, string][]
}[] = [
  {
    title: 'USER ACCESS REQUEST',
    fields: [
      ['requestType', 'Request Type'],
      ['requestor', 'Requestor'],
      ['requestedUsers', 'Requested User(s)'],
      ['platform', 'Platform'],
      ['accessLevel', 'Access Level Needed'],
      ['similarAccessAs', 'Similar Access As'],
      ['businessJustification', 'Business Justification'],
      ['startDate', 'Start Date'],
      ['approvalObtained', 'Approval Obtained'],
    ],
  },
]

export const USER_ACCESS_SUBJECT = 'Prism Portal Request - User Access'

export function buildUserAccessEmailBody(values: UserAccessValues): string {
  const blocks: string[] = []
  for (const section of SUMMARY_SECTIONS) {
    const filled = section.fields.filter(([key]) => {
      const val = values[key]
      if (Array.isArray(val)) return val.length > 0
      return String(val ?? '').trim().length > 0
    })
    if (filled.length === 0) continue
    const lines = [section.title]
    for (const [key, label] of filled) {
      const val = values[key]
      const display = Array.isArray(val) ? val.join(', ') : String(val).trim()
      lines.push(`  ${label}: ${display}`)
    }
    blocks.push(lines.join('\r\n'))
  }
  return blocks.join('\r\n\r\n')
}

export function buildUserAccessOutlookUrl(
  values: UserAccessValues,
  loginHint?: string | null
): string {
  const params = new URLSearchParams({
    to: REQUEST_RECIPIENT,
    subject: USER_ACCESS_SUBJECT,
    body: buildUserAccessEmailBody(values),
  })
  if (loginHint && loginHint.trim()) {
    params.set('login_hint', loginHint.trim())
  }
  const query = params.toString().replace(/\+/g, '%20')
  return `${OUTLOOK_COMPOSE_BASE}?${query}`
}
